import { useCallback, useEffect, useRef, useState } from "react";

import nowInMs from "../Utils/nowInMs";
import useAudioSignalMonitor from "./useAudioSignalMonitor";
import useBrowserAudioRecorder from "./useBrowserAudioRecorder";
import useSpeechToTextRecorderActions from "./useSpeechToTextRecorderActions";
import useSpeechToTextRecorderProcessing from "./useSpeechToTextRecorderProcessing";
import useSpeechTranscriptionRequest from "./useSpeechTranscriptionRequest";

export const STT_RECORDER_STATE_IDLE = "idle";
export const STT_RECORDER_STATE_RECORDING = "recording";
export const STT_RECORDER_STATE_PAUSED = "paused";
export const STT_RECORDER_STATE_TRANSCRIBING = "transcribing";

export const SILENCE_RMS_THRESHOLD = 0.03;
export const MIN_RECORDING_DURATION_MS = 500;
export const MAX_RECORDING_DURATION_MS = 20 * 60 * 1000;
export const RECORDING_WARNING_WINDOW_MS = 2 * 60 * 1000;

// This hook orchestrates recorder lifecycle:
// browser APIs -> signal detection -> API transcription -> UI state updates.
const useSpeechToTextRecorder = ({
  resourceId,
  onResult,
  onRecordingReady,
  onError,
  onStateChange,
  disabled = false
}) => {
  const [state, setRecorderState] = useState(STT_RECORDER_STATE_IDLE);
  const [recordingDurationMs, setRecordingDurationMs] = useState(0);
  const recordingStartedAtRef = useRef(null);
  const recordingDurationRef = useRef(0);
  const recordingTickStartedAtRef = useRef(null);
  const maxDurationReachedRef = useRef(false);
  const recordingDurationLimitMs = MAX_RECORDING_DURATION_MS;

  const {
    appendRecordingChunk,
    clearDiscardRecordingFlag,
    clearMediaRecorder,
    consumeRecordedData,
    createMediaRecorder,
    isMediaRecorderActive,
    isMediaRecorderSupported,
    markDiscardNextRecording,
    mediaRecorderRef,
    mediaStreamRef,
    recordingHandled,
    resetRecordingSession,
    setRecordingHandled,
    shouldDiscardRecording,
    stopActiveMediaRecorder,
    stopMediaStreamTracks
  } = useBrowserAudioRecorder();

  const { hasAudioSignalRef, startSignalMonitor, stopSignalMonitor } =
    useAudioSignalMonitor();

  const requestTranscription = useSpeechTranscriptionRequest(resourceId);

  const resetRecordingDuration = useCallback(() => {
    recordingDurationRef.current = 0;
    recordingTickStartedAtRef.current = null;
    maxDurationReachedRef.current = false;
    setRecordingDurationMs(0);
  }, []);

  const processRecordedAudio = useSpeechToTextRecorderProcessing({
    clearDiscardRecordingFlag,
    consumeRecordedData,
    hasAudioSignalRef,
    onError,
    onRecordingReady,
    onResult,
    recordingHandled,
    recordingStartedAtRef,
    requestTranscription,
    setRecorderState,
    setRecordingHandled,
    shouldDiscardRecording,
    stopMediaStreamTracks,
    stopSignalMonitor
  });

  const {
    discardCurrentRecording,
    finalizeRecording,
    pauseRecording,
    restartRecording,
    resumeRecording,
    startRecording
  } = useSpeechToTextRecorderActions({
    appendRecordingChunk,
    clearMediaRecorder,
    createMediaRecorder,
    disabled,
    isMediaRecorderActive,
    markDiscardNextRecording,
    mediaRecorderRef,
    mediaStreamRef,
    onError,
    onRecordingSessionStart: resetRecordingDuration,
    processRecordedAudio,
    recordingStartedAtRef,
    resetRecordingSession,
    resourceId,
    setRecorderState,
    startSignalMonitor,
    stopMediaStreamTracks,
    stopSignalMonitor
  });

  useEffect(() => {
    if (state !== STT_RECORDER_STATE_RECORDING) {
      recordingTickStartedAtRef.current = null;
      return undefined;
    }

    recordingTickStartedAtRef.current = nowInMs();
    const intervalId = setInterval(() => {
      const currentTime = nowInMs();
      const startedAt = recordingTickStartedAtRef.current || currentTime;
      const elapsedSinceLastTick = Math.max(0, currentTime - startedAt);

      recordingTickStartedAtRef.current = currentTime;
      recordingDurationRef.current += elapsedSinceLastTick;
      setRecordingDurationMs(recordingDurationRef.current);
    }, 250);

    return () => clearInterval(intervalId);
  }, [state]);

  useEffect(() => {
    if (state === STT_RECORDER_STATE_IDLE) {
      resetRecordingDuration();
    }
  }, [resetRecordingDuration, state]);

  useEffect(() => {
    if (state !== STT_RECORDER_STATE_RECORDING) return;
    if (maxDurationReachedRef.current) return;
    if (recordingDurationMs < recordingDurationLimitMs) return;

    maxDurationReachedRef.current = true;
    finalizeRecording();
  }, [finalizeRecording, recordingDurationLimitMs, recordingDurationMs, state]);

  useEffect(() => {
    onStateChange?.(state);
  }, [onStateChange, state]);

  useEffect(() => {
    return () => {
      recordingStartedAtRef.current = null;

      if (isMediaRecorderActive()) {
        stopActiveMediaRecorder({ detachOnStop: true });
      }

      // Unmount cleanup must leave no active recorder/stream/analyser in browser.
      clearMediaRecorder();
      stopSignalMonitor();
      stopMediaStreamTracks();
    };
  }, [
    clearMediaRecorder,
    isMediaRecorderActive,
    stopActiveMediaRecorder,
    stopMediaStreamTracks,
    stopSignalMonitor
  ]);

  return {
    discardCurrentRecording,
    finalizeRecording,
    isMediaRecorderSupported,
    pauseRecording,
    recordingDurationMs,
    recordingDurationLimitMs,
    restartRecording,
    resumeRecording,
    startRecording,
    state
  };
};

export default useSpeechToTextRecorder;
